/**
 * twrd-contact — the deterministic answers on /pages/contact and /pages/track-your-order.
 *
 *   1. Order status. Posts to the same /api/order-lookup endpoint the chat
 *      widget uses. No model is involved on that path, on either side.
 *   2. Size. Pure client-side arithmetic over the height/weight heuristic and
 *      the flat-measured charts in snippets/twrd-size-charts.liquid.
 *
 * The chat guide stays available under both, as the second choice.
 *
 * Rendering rule for anything that came off the wire: textContent only, never
 * innerHTML. The one exception is a tracking link, and it only becomes an <a>
 * after safeTrackingUrl() proves it parses as https.
 */
(function () {
  'use strict';

  // One tools root per page. /pages/contact is #twrd-contact, /pages/track-your-order is
  // #twrd-track. Both carry data-twrd-tools-root, and both render the same
  // snippets/twrd-order-lookup.liquid markup, so this binds either one.
  var root = document.querySelector('[data-twrd-tools-root]') || document.getElementById('twrd-contact');
  if (!root) return;

  // DOM-clobbering guard, ported from twrd-chat-widget.js. window.THWRD_CHAT_API is
  // honoured only when it is a real string and names the production API, or a
  // localhost origin while this script itself is served from localhost. An element
  // with id="THWRD_CHAT_API" is not a string and is ignored, so order lookups (which
  // carry email and order number) can never be pointed at another host.
  var PROD_API_ORIGIN = 'https://chat.thwrd.com';
  var SCRIPT_EL = document.currentScript;
  var scriptOrigin = '';
  try { scriptOrigin = (SCRIPT_EL && SCRIPT_EL.src) ? new URL(SCRIPT_EL.src).origin : ''; } catch (e) {}
  var isLocalDev = scriptOrigin.indexOf('http://localhost') === 0 || scriptOrigin.indexOf('http://127.') === 0;
  function trustedApiOverride(value, localDev) {
    if (typeof value !== 'string') return '';
    if (value === PROD_API_ORIGIN) return value;
    if (localDev && /^http:\/\/(localhost|127\.0\.0\.1)(:\d{1,5})?$/.test(value)) return value;
    return '';
  }
  var API_BASE = trustedApiOverride(window.THWRD_CHAT_API, isLocalDev) || PROD_API_ORIGIN;
  var ORDER_LOOKUP_URL = API_BASE + '/api/order-lookup';
  var SUPPORT_EMAIL = 'info@thwrd.com';

  // Mirrors widget.js. Same key, so a visitor who used the chat first keeps one
  // session instead of getting a second one here.
  var CHAT_SESSION_KEY = 'twrd_chat_session_id_v1';
  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  var ORDER_GENERIC_ERROR =
    'We could not reach the order system just now. Try again in a moment, or email ' + SUPPORT_EMAIL + '.';

  // -----------------------------------------------------------------------
  // Small helpers
  // -----------------------------------------------------------------------
  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  /** Mirrors generateSessionId() in widget.js: RFC4122 v4. */
  function generateSessionId() {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch (e) {}
    var bytes = new Uint8Array(16);
    try {
      (window.crypto || crypto).getRandomValues(bytes);
    } catch (e2) {
      for (var i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = Array.prototype.map
      .call(bytes, function (b) { return b.toString(16).padStart(2, '0'); })
      .join('');
    return (
      hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' +
      hex.slice(16, 20) + '-' + hex.slice(20)
    );
  }

  /**
   * The server refuses a lookup without a valid session id (400
   * session_required), so the id has to be the same shape the widget produces.
   * Reuse the stored one when it is well formed, mint one otherwise.
   */
  function currentSessionId() {
    var existing = null;
    try { existing = sessionStorage.getItem(CHAT_SESSION_KEY); } catch (e) {}
    if (existing && UUID_RE.test(existing)) return existing;
    var fresh = generateSessionId();
    try { sessionStorage.setItem(CHAT_SESSION_KEY, fresh); } catch (e2) {}
    return fresh;
  }

  /** Mirrors sanitizeOrderNumber() in api/order-lookup.ts. UX only. */
  function validateOrderNumberInput(raw) {
    if (typeof raw !== 'string') return null;
    var cleaned = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleaned.length < 3 || cleaned.length > 15) return null;
    if (!/\d{3,}/.test(cleaned)) return null;
    return cleaned;
  }

  /** Mirrors sanitizeEmail() in api/order-lookup.ts. UX only. */
  function validateEmailInput(raw) {
    if (typeof raw !== 'string') return null;
    var email = raw.trim().toLowerCase();
    if (email.length > 254) return null;
    if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(email)) return null;
    return email;
  }

  /**
   * Mirrors safeTrackingUrl() in widget.js and lib/tracking-url.ts. A tracking
   * URL is semi-trusted: it reaches us from a carrier app through Shopify. Only
   * a parseable https: URL may become an href.
   */
  function safeTrackingUrl(raw) {
    if (typeof raw !== 'string') return null;
    var trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      var url = new URL(trimmed);
      if (url.protocol !== 'https:') return null;
      return url.toString();
    } catch (e) {
      return null;
    }
  }

  /** Opens the chat guide, and asks it the given quick reply when it appears. */
  function openGuide(label) {
    var toggle = document.getElementById('twrdToggle');
    var panel = document.getElementById('twrdPanel');
    if (!toggle || !panel) return false;
    if (!panel.classList.contains('open')) toggle.click();
    if (!label) return true;
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      var quick = document.getElementById('twrdQuick');
      if (quick) {
        var buttons = quick.querySelectorAll('button');
        for (var i = 0; i < buttons.length; i += 1) {
          if ((buttons[i].textContent || '').trim().toLowerCase() === label.toLowerCase()) {
            clearInterval(timer);
            buttons[i].click();
            return;
          }
        }
      }
      if (tries > 16) clearInterval(timer);
    }, 120);
    return true;
  }

  root.querySelectorAll('[data-twrd-guide]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (!openGuide(btn.getAttribute('data-twrd-guide'))) {
        var mail = 'mailto:' + SUPPORT_EMAIL;
        window.location.href = mail;
      }
    });
  });

  // -----------------------------------------------------------------------
  // 1. Order status
  // -----------------------------------------------------------------------
  (function orderTool() {
    var form = root.querySelector('[data-twrd-order-form]');
    if (!form) return;

    var numberInput = form.querySelector('#twrd-order-number');
    var emailInput = form.querySelector('#twrd-order-email');
    var submit = form.querySelector('[data-twrd-order-submit]');
    var errorEl = root.querySelector('[data-twrd-order-error]');
    var resultEl = root.querySelector('[data-twrd-order-result]');

    function showError(text) {
      clear(resultEl);
      resultEl.hidden = true;
      errorEl.textContent = String(text);
      errorEl.hidden = false;
    }

    function hideError() {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function row(label, value) {
      var wrap = el('div', 'twrd-c__orow');
      wrap.appendChild(el('span', 'twrd-c__olabel', label));
      wrap.appendChild(el('span', 'twrd-c__ovalue', value));
      return wrap;
    }

    function renderOrder(order) {
      hideError();
      clear(resultEl);
      resultEl.hidden = false;

      resultEl.appendChild(el('h4', 'twrd-c__otitle', 'Order ' + String(order.name || '')));
      if (order.placed) resultEl.appendChild(row('Placed', String(order.placed)));

      if (order.cancelledAt) {
        resultEl.appendChild(row('Status', 'Cancelled on ' + String(order.cancelledAt)));
      } else {
        if (order.financialStatus) resultEl.appendChild(row('Payment', String(order.financialStatus)));
        if (order.fulfillmentStatus) resultEl.appendChild(row('Fulfillment', String(order.fulfillmentStatus)));
        if (order.totalPrice && order.currency) {
          resultEl.appendChild(row('Total', String(order.totalPrice) + ' ' + String(order.currency)));
        }

        if (Array.isArray(order.items) && order.items.length > 0) {
          var wrap = el('div', 'twrd-c__orow');
          wrap.appendChild(el('span', 'twrd-c__olabel', 'Items'));
          var list = el('span', 'twrd-c__ovalue');
          order.items.forEach(function (it) {
            if (!it || typeof it !== 'object') return;
            var qty = typeof it.quantity === 'number' && isFinite(it.quantity) ? it.quantity : 1;
            var line = qty + ' x ' + String(it.title || 'item');
            if (it.variant) line += ' (' + String(it.variant) + ')';
            list.appendChild(el('span', 'twrd-c__oitem', line));
          });
          wrap.appendChild(list);
          resultEl.appendChild(wrap);
        }

        if (order.carrier) resultEl.appendChild(row('Carrier', String(order.carrier)));

        var href = safeTrackingUrl(order.trackingUrl);
        if (href) {
          var trackRow = el('div', 'twrd-c__orow');
          trackRow.appendChild(el('span', 'twrd-c__olabel', 'Track it'));
          var value = el('span', 'twrd-c__ovalue');
          var link = el('a', 'twrd-c__olink',
            order.carrier ? 'Open ' + String(order.carrier) + ' tracking' : 'Open tracking page');
          link.href = href;
          link.setAttribute('rel', 'noopener noreferrer');
          link.setAttribute('target', '_blank');
          value.appendChild(link);
          trackRow.appendChild(value);
          resultEl.appendChild(trackRow);
        } else if (typeof order.trackingUrl === 'string' && order.trackingUrl) {
          // Not https, so it never becomes a link. Shown as text rather than
          // hidden, so the visitor still sees what the carrier sent.
          resultEl.appendChild(row('Track it', String(order.trackingUrl)));
        }

        if (order.shipmentStatus) resultEl.appendChild(row('Shipment', String(order.shipmentStatus)));
        if (order.shipmentUpdated) resultEl.appendChild(row('Updated', String(order.shipmentUpdated)));
        if (order.destination) resultEl.appendChild(row('Destination', String(order.destination)));

        if (!order.carrier && !order.trackingUrl && order.fulfillmentStatus === 'unfulfilled') {
          resultEl.appendChild(el('p', 'twrd-c__onote',
            'No shipment yet. Still in production or waiting to ship.'));
        }
      }

      resultEl.appendChild(el('p', 'twrd-c__onote',
        'Still stuck? Email ' + SUPPORT_EMAIL + ' and we will look into it directly.'));
      try { resultEl.focus({ preventScroll: true }); } catch (e) {}
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      hideError();

      var orderNumber = validateOrderNumberInput(numberInput.value);
      if (!orderNumber) {
        showError('That order number does not look right. It is on your confirmation email, like #WRD1109.');
        numberInput.focus();
        return;
      }
      var email = validateEmailInput(emailInput.value);
      if (!email) {
        showError('Enter the email address you used for the order.');
        emailInput.focus();
        return;
      }

      submit.disabled = true;
      var label = submit.textContent;
      submit.textContent = 'Checking...';

      var sid = currentSessionId();
      fetch(ORDER_LOOKUP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sid },
        body: JSON.stringify({ orderNumber: orderNumber, email: email, sessionId: sid }),
      })
        .then(function (res) {
          return res.json()
            .catch(function () { return null; })
            .then(function (data) { return { ok: res.ok, status: res.status, data: data }; });
        })
        .catch(function () { return { ok: false, status: 0, data: null }; })
        .then(function (res) {
          submit.disabled = false;
          submit.textContent = label;

          var data = res.data && typeof res.data === 'object' ? res.data : null;
          if (!data) {
            showError(ORDER_GENERIC_ERROR);
            return;
          }
          // Rate limited, bad session, Shopify down, or a real mismatch: show
          // what the server said. Never a silent failure, never our own guess.
          if (!res.ok || data.found === false) {
            var serverMsg =
              (typeof data.message === 'string' && data.message) ||
              (typeof data.error === 'string' && data.error) ||
              ORDER_GENERIC_ERROR;
            showError(serverMsg);
            return;
          }
          if (data.order && typeof data.order === 'object') {
            renderOrder(data.order);
          } else {
            showError(ORDER_GENERIC_ERROR);
          }
        });
    });
  })();

  // -----------------------------------------------------------------------
  // 2. Size calculator
  // -----------------------------------------------------------------------
  (function sizeTool() {
    var form = root.querySelector('[data-twrd-size-form]');
    if (!form) return;

    var dataEl = document.querySelector('[data-twrd-size-charts]');
    var charts = {};
    try { charts = JSON.parse(dataEl.textContent); } catch (e) { charts = {}; }

    var pieceInput = form.querySelector('#twrd-size-piece');
    var heightInput = form.querySelector('#twrd-size-height');
    var heightInInput = form.querySelector('#twrd-size-height-in');
    var weightInput = form.querySelector('#twrd-size-weight');
    var fitInput = form.querySelector('#twrd-size-fit');
    var unitInputs = form.querySelectorAll('input[name="twrd-size-unit"]');
    var heightLabel = form.querySelector('[data-twrd-height-label]');
    var weightLabel = form.querySelector('[data-twrd-weight-label]');
    var errorEl = root.querySelector('[data-twrd-size-error]');
    var resultEl = root.querySelector('[data-twrd-size-result]');

    var ORDER = ['S', 'M', 'L', 'XL', '2XL'];

    // The height/weight heuristic from docs/system-prompt.md, as bands.
    // Each band's upper bound is exclusive.
    var HEIGHT_BANDS = [170, 178, 183, 188];   // cm
    var WEIGHT_BANDS = [65, 75, 85, 92];       // kg

    function bandOf(value, bounds) {
      for (var i = 0; i < bounds.length; i += 1) {
        if (value < bounds[i]) return i;
      }
      return bounds.length;
    }

    function currentUnit() {
      for (var i = 0; i < unitInputs.length; i += 1) {
        if (unitInputs[i].checked) return unitInputs[i].value;
      }
      return 'metric';
    }

    function syncUnitLabels() {
      var metric = currentUnit() === 'metric';
      heightLabel.textContent = metric ? 'Height (cm)' : 'Height (ft / in)';
      // Imperial: feltet til venstre bliver fod, og tomme-feltet foldes ud ved
      // siden af. 5 ft 11 in er tallet folk kender, 71 inches er det ikke.
      if (heightInInput) {
        heightInInput.hidden = metric;
        if (metric) heightInInput.value = '';
      }
      weightLabel.textContent = metric ? 'Weight (kg)' : 'Weight (lbs)';
      heightInput.placeholder = metric ? '180' : '5';
      weightInput.placeholder = metric ? '80' : '176';
    }

    for (var u = 0; u < unitInputs.length; u += 1) {
      unitInputs[u].addEventListener('change', syncUnitLabels);
    }
    syncUnitLabels();

    function showError(text) {
      clear(resultEl);
      resultEl.hidden = true;
      errorEl.textContent = String(text);
      errorEl.hidden = false;
    }

    function line(chart, size) {
      var values = chart.rows[size];
      if (!values) return null;
      var parts = [];
      for (var i = 0; i < chart.columns.length; i += 1) {
        if (typeof values[i] !== 'number') continue;
        parts.push(chart.columns[i].toLowerCase() + ' ' + values[i] + ' cm');
      }
      var text = size + ': ' + parts.join(', ') + ' (laid flat).';
      if (chart.columns[0] === 'Chest' && typeof values[0] === 'number') {
        text += ' That is ' + (values[0] * 2) + ' cm around the chest.';
      }
      return text;
    }

    var HOW_IT_SITS = {
      tee: 'Shoulders drop past the natural line, the body sits boxy, the hem lands at mid-hip and the sleeve stops near the elbow.',
      hoodie: 'Boxy through the torso with a dropped shoulder, hem past the hip, sleeve at the mid-forearm. Heavy cotton, it holds its shape.',
      sweatshirt: 'Boxy crew neck with a dropped shoulder, drapes past the hip. Heavyweight, so it keeps its line with wear.',
      sweatpants: 'Relaxed through the thigh with a light taper. Waist is elasticated, so the waist number is a guide, not a limit.',
    };

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      errorEl.hidden = true;

      var kind = pieceInput.value;
      var chart = charts[kind];
      if (!chart) {
        showError('Pick which piece you are sizing.');
        return;
      }

      var height = parseFloat(heightInput.value);
      var weight = parseFloat(weightInput.value);
      if (!isFinite(height) || !isFinite(weight) || height <= 0 || weight <= 0) {
        showError('Enter your height and your weight, and we will do the rest.');
        return;
      }

      var metric = currentUnit() === 'metric';
      // Imperial hoejde kommer som fod plus tommer. Tomt tomme-felt er 0, ikke
      // en fejl: 6 ft flat er et gyldigt svar.
      var extraInches = 0;
      if (!metric && heightInInput) {
        var raw = heightInInput.value.trim();
        if (raw !== '') {
          extraInches = parseFloat(raw);
          if (!isFinite(extraInches) || extraInches < 0 || extraInches >= 12) {
            showError('Inches go from 0 to 11. For 5 ft 11 in, write 5 and 11.');
            return;
          }
        }
      }
      var heightCm = metric ? height : ((height * 12) + extraInches) * 2.54;
      var weightKg = metric ? weight : weight * 0.45359237;

      if (heightCm < 120 || heightCm > 230 || weightKg < 30 || weightKg > 250) {
        showError('Those numbers land outside the chart. Write to ' + SUPPORT_EMAIL + ' and a person will size it with you.');
        return;
      }

      var available = ORDER.filter(function (s) { return chart.rows[s]; });
      var maxIndex = available.length - 1;

      var hBand = bandOf(heightCm, HEIGHT_BANDS);
      var wBand = bandOf(weightKg, WEIGHT_BANDS);

      // Husets raad: staar du MELLEM TO stoerrelser, tag den mindre. Snittet
      // er allerede oversized, saa det store baand giver et telt.
      //
      // Reglen gaelder kun naar de to baand er naboer. Ligger de laengere fra
      // hinanden, er kroppen ikke proportional, og saa maa vaegten tale med:
      // en paa 95 kg passer ikke i en M, uanset hvad hoejden siger. Der tager
      // vi midtpunktet, rundet ned, saa raadet stadig haelder mod det mindre.
      var spread = Math.abs(hBand - wBand);
      var basePick = spread <= 1
        ? Math.min(hBand, wBand)
        : Math.floor((hBand + wBand) / 2);
      var baseAlt = spread <= 1
        ? Math.max(hBand, wBand)
        : Math.ceil((hBand + wBand) / 2);

      var adjust = fitInput.value === 'closer' ? -1 : fitInput.value === 'fuller' ? 1 : 0;

      function clamp(i) { return Math.max(0, Math.min(maxIndex, i)); }

      var pickIndex = clamp(basePick + adjust);
      var altIndex = clamp(baseAlt + adjust);
      var pick = available[pickIndex];
      var alt = available[altIndex];

      clear(resultEl);
      resultEl.hidden = false;

      var head = el('p', 'twrd-c__spick');
      head.appendChild(el('span', 'twrd-c__spicklabel', 'Your size'));
      head.appendChild(el('strong', 'twrd-c__spicksize', pick));
      head.appendChild(el('span', 'twrd-c__spickkind', 'in the ' + chart.label.toLowerCase()));
      resultEl.appendChild(head);

      var chartLine = line(chart, pick);
      if (chartLine) resultEl.appendChild(el('p', 'twrd-c__sline', chartLine));

      if (HOW_IT_SITS[kind]) resultEl.appendChild(el('p', 'twrd-c__sline', HOW_IT_SITS[kind]));

      if (alt !== pick) {
        var altBox = el('div', 'twrd-c__salt');
        altBox.appendChild(el('p', 'twrd-c__salth', 'You are between ' + alt + ' and ' + pick + '.'));
        altBox.appendChild(el('p', 'twrd-c__sline',
          pick + ' is the one we would hand you. The pieces already run large, so it sits boxy without swallowing you. ' +
          alt + ' gives more length and a heavier drape, if that is the look you are after.'));
        var altLine = line(chart, alt);
        if (altLine) altBox.appendChild(el('p', 'twrd-c__sline', altLine));
        resultEl.appendChild(altBox);
      }

      if (pickIndex === maxIndex && basePick + adjust > maxIndex) {
        resultEl.appendChild(el('p', 'twrd-c__onote',
          pick + ' is the largest size this piece is made in. If it needs to run bigger, write to ' + SUPPORT_EMAIL + ' before you order.'));
      }

      resultEl.appendChild(el('p', 'twrd-c__onote',
        'This is guidance, not a measurement. The chart itself varies by up to 2.5 cm, and every piece is made after you order, so the size is locked at checkout.'));

      try { resultEl.focus({ preventScroll: true }); } catch (e) {}
    });
  })();

  // -----------------------------------------------------------------------
  // 3. Contact form: land on the outcome, and let a card jump to the form
  // -----------------------------------------------------------------------
  var posted = root.querySelector('[data-twrd-posted], [data-twrd-errors]');
  if (posted) {
    try {
      posted.focus({ preventScroll: true });
      posted.scrollIntoView({ block: 'center' });
    } catch (e) {}
  }

  root.querySelectorAll('[data-twrd-form-jump]').forEach(function (card) {
    card.addEventListener('click', function () {
      var first = root.querySelector('select[name="contact[topic]"], .twrd-c__done, .twrd-c__alert');
      var target = first || root.querySelector('.twrd-c__band');
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (first && first.tagName === 'SELECT') {
        try { first.focus({ preventScroll: true }); } catch (e) {}
      }
    });
  });

  // ?topic=carrier preselects the topic and brings the form into view, so a
  // link from another page (the Carriers page's "Become a carrier") lands on
  // the right form instead of opening a mail app that many visitors lack.
  var TOPICS = { carrier: 'Carrying or partnering' };
  var wanted = null;
  try { wanted = TOPICS[new URLSearchParams(window.location.search).get('topic')] || null; } catch (e) {}
  var topicSelect = root.querySelector('select[name="contact[topic]"]');
  if (wanted && topicSelect && !posted) {
    Array.prototype.forEach.call(topicSelect.options, function (o) { o.selected = o.value === wanted; });
    try {
      topicSelect.scrollIntoView({ block: 'center' });
      topicSelect.focus({ preventScroll: true });
    } catch (e) {}
  }
})();
